import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, CheckCircle2, AlertTriangle, Download, LogOut, FileCheck2, Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/api";

function Row({ c }) {
  const Icon = c.ok ? CheckCircle2 : AlertTriangle;
  return (
    <div data-testid={`posture-${c.key}`} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${c.ok ? "text-emerald-600" : "text-amber-500"}`} />
      <div className="min-w-0">
        <div className="text-sm text-slate-800">{c.label}</div>
        <div className="text-xs text-slate-500">{c.detail}</div>
      </div>
    </div>
  );
}

export default function SecurityPostureCard({ user }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verify, setVerify] = useState(null);
  const load = () => api.get("/security/posture").then((r) => setP(r.data)).catch(() => setP(null));
  useEffect(() => { load(); }, []);
  if (!p) return null;
  const okCount = p.checks.filter((c) => c.ok).length;

  const toggleMfa = async (on) => {
    setBusy(true);
    try { await api.put("/security/require-mfa", { on }); toast.success(on ? "MFA now required for owner & admin" : "MFA no longer required"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not change"); }
    finally { setBusy(false); }
  };
  const revoke = async (all) => {
    setBusy(true);
    try { const r = await api.post("/security/sessions/revoke-others", { all }); toast.success(`Signed out ${r.data.revoked} session(s)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not sign out sessions"); }
    finally { setBusy(false); }
  };
  const runVerify = async () => {
    setBusy(true);
    try { const r = await api.get("/security/audit-log/verify"); setVerify(r.data); }
    catch { toast.error("Could not verify"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6" data-testid="security-posture-card">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#003E87]" /><h2 className="font-semibold text-black">Security &amp; compliance</h2></div>
        <span data-testid="posture-score" className={`text-xs font-semibold rounded px-2 py-0.5 ${okCount === p.checks.length ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          {okCount} / {p.checks.length} controls in place
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        The technical controls a SOC 2 audit looks at (CC6 access, CC7 monitoring, confidentiality). Certification itself is issued to the company by an audit firm — see the list at the bottom for what sits outside the software.
      </p>
      <div className="mb-4">{p.checks.map((c) => <Row key={c.key} c={c} />)}</div>

      <div className="flex flex-wrap items-center gap-4 mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="flex items-center gap-3">
          <Switch data-testid="require-mfa-switch" checked={p.require_mfa_privileged} disabled={busy || user?.role !== "owner"} onCheckedChange={toggleMfa} />
          <div>
            <div className="text-sm text-slate-800">Require MFA for owner &amp; admin</div>
            <div className="text-xs text-slate-500">{user?.role === "owner" ? "Privileged people must set up an authenticator before using the app." : "Only the owner can change this."}</div>
          </div>
        </div>
        <div className="text-xs text-slate-500 ml-auto" data-testid="active-sessions">{p.active_sessions} active session(s) · {p.people_without_mfa} without MFA</div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button data-testid="revoke-my-sessions" variant="outline" size="sm" disabled={busy} onClick={() => revoke(false)}><LogOut className="w-3.5 h-3.5 mr-1.5" />Sign out my other devices</Button>
        {user?.role === "owner" && <Button data-testid="revoke-all-sessions" variant="outline" size="sm" disabled={busy} onClick={() => revoke(true)} className="text-rose-600 border-rose-200 hover:bg-rose-50"><LogOut className="w-3.5 h-3.5 mr-1.5" />Sign out everyone else</Button>}
        <Button data-testid="verify-audit-log" variant="outline" size="sm" disabled={busy} onClick={runVerify}>{busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileCheck2 className="w-3.5 h-3.5 mr-1.5" />}Verify audit log</Button>
        <a data-testid="export-audit-log" href={`${API_BASE}/security/audit-log/export.csv?days=365`} className="inline-flex items-center h-8 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium hover:bg-slate-50"><Download className="w-3.5 h-3.5 mr-1.5" />Export audit log (CSV)</a>
      </div>
      {verify && (
        <div data-testid="audit-verify-result" className={`mt-3 text-xs rounded-md px-3 py-2 ${verify.ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
          {verify.ok ? "No tampering detected" : `${verify.tampered} entries fail verification`} — {verify.signed} signed of {verify.checked} checked{verify.unsigned_legacy ? ` (${verify.unsigned_legacy} predate signing)` : ""}.
        </div>
      )}

      <div className="mt-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Outside the software — needed for the certificate</div>
        <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4">
          {p.organizational.map((o, i) => <li key={i} data-testid={`posture-org-${i}`}>{o}</li>)}
        </ul>
      </div>
    </Card>
  );
}
